import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";

import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import instance from "../../../globals/axios";
import EditIcon from "../../../assets/images/NewEditIcon.png";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const FertilizerPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const { showAlert } = useAlert();
  const [comment, setComment] = useState("");

  let farmerPlotId = getFarmerPlotId();
  const [daysOfCrop, setDaysOfCrop] = useState(null);
  const [fertilizerNames, setFertilizerNames] = useState([]);
  const [dose, setDose] = useState([]);
  const [recommendedDate, setRecommendedDate] = useState(null);
  const [userSelectFertilizer, setUserSelectFertilizer] = useState([]); // State for user-selected fertilizers
  const [userDose, setUserDose] = useState([]);
  const [fertilizerData, setFertilizerData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFertilizer, setEditedFertilizer] = useState(null);
  const [isChecked, setIsChecked] = useState([]);
  const [id, setId] = useState("");
  const [activityDateError, setActivityDateError] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [fertilizerDropdown, setFertilizerDropdown] = useState([]);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setActivityDateError(false);
    } else {
      setActivityDateError(true);
    }
  };
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const scrollableContainerStyle = {
    maxHeight: "140px",
    overflowY: "auto",
  };
  const handleCheck = (event, index) => {
    const checkedState = event.target.checked;
    const updatedChecked = [...isChecked];
    updatedChecked[index] = checkedState;
    setIsChecked(updatedChecked);
  };

  const handleFertilizerChange = (event, index) => {
    const selectedFertilizer = event.target.value;
    const updatedUserSelectFertilizer = [...userSelectFertilizer];
    updatedUserSelectFertilizer[index] = selectedFertilizer;
    setUserSelectFertilizer(updatedUserSelectFertilizer);
  };
  const handleDoseChange = (event, index) => {
    // const selectedDose = event.target.value;
    // setUserDose([selectedDose]);
    const selectedDose = event.target.value;
    const updateSelectedDose = [...userDose];
    updateSelectedDose[index] = selectedDose;
    setUserDose(updateSelectedDose);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-fertilizer?farmer_plot_id=${farmerPlotId}`
        );
        let data = response.data;
        setFertilizerData(data);

        if (data.current_fertilizer) {
          setDaysOfCrop(data.current_fertilizer.days);
          setRecommendedDate(data.current_fertilizer.recommended_date);
          const editedFertilizers = data.current_fertilizer.fertilizers;
          const names = editedFertilizers.map(
            (fertilizer) => fertilizer.fertilizer_name
          );
          const doses = editedFertilizers.map((fertilizer) => fertilizer.dose);
          setFertilizerNames(names);
          setDose(doses);
        } else {
          // Logic when current_fertilizer is empty
          setFertilizerNames([]);
          setDose([]);
          setUserSelectFertilizer([]);
          setUserDose([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    const fetchFertilizerDropdownData = async () => {
      try {
        const response = await instance(
          `/plotengagement/get-fertilizer-names?stage=FERTILIZER`
        );
        let data = response.data;
        setFertilizerDropdown(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
      fetchFertilizerDropdownData();
    }
  }, [farmerPlotId]);
  const handleEditClick = async (fertilizerId) => {
    try {
      const response = await instance.get(
        `/plotengagement/get-fertilizer-by-id?farmer_plot=${farmerPlotId}&fertilizer_id=${fertilizerId}`
      );
      const fetchedData = response.data;
      const recommendationNames = [];
      const recommendationDoses = [];
      setEditedFertilizer(fetchedData);

      fetchedData.forEach((data) => {
        data.fertilizers.forEach((fertilizer) => {
          if (fertilizer.type === "user") {
            userDose.push(fertilizer.dose);
            userSelectFertilizer.push(fertilizer.fertilizer_name);
          } else if (fertilizer.type === "recommend") {
            recommendationNames.push(fertilizer.fertilizer_name);
            recommendationDoses.push(fertilizer.dose);
          }
          if (data.activity_date) {
            const activityDate = new Date(data.activity_date);
            setSelectedDate(activityDate);
          }
          if (data.recommended_date) {
            setRecommendedDate(data.recommended_date);
          }
        });
      });
      setDaysOfCrop(fetchedData[0].days);
      // Now you can handle multiple recommendations in your state or other logic
      setId(fetchedData[0].id);
      setFertilizerNames(recommendationNames); // Array of recommendation names
      setDose(recommendationDoses);
      setComment(fetchedData[0].comments);
      setIsEditMode(true);
    } catch (error) {
      console.error("Error fetching fertilizer data:", error);
    }
  };

  const handleSubmit = () => {
    if (!selectedDate) {
      setActivityDateError(true);
      return;
    } else {
      setActivityDateError(false);

      setErrors({});
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      if (selectedDate) {
        const adjustedDate = new Date(selectedDate);
        adjustedDate.setDate(adjustedDate.getDate());
      }
      const data = {
        days: daysOfCrop,
        recommended_date: recommendedDate,
        activity_date: formatDate(selectedDate),
        fertilizers: [],
        comments: comment,
      };
      for (let i = 0; i < fertilizerNames.length; i++) {
        const fertilizerObject = {
          fertilizer_name: fertilizerNames[i],
          dose: dose[i],
          type: "recommend",
        };
        data.fertilizers.push(fertilizerObject);
      }
      if (isChecked && userSelectFertilizer.length > 0) {
        for (let i = 0; i < userSelectFertilizer.length; i++) {
          const fertilizerObject = {
            fertilizer_name: userSelectFertilizer[i],
            dose: userDose[i],
            type: "user",
            other_for: fertilizerNames[i],
          };
          data.fertilizers.push(fertilizerObject);
        }
      }
      if (isEditMode) {
        data.id = id;
        data.farmer_plot_id = farmerPlotId;
      } else {
        data.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-fertilizer-by-id"
        : "plotengagement/create-fertilizer";
      instance
        .post(apiUrl, data)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Error in updating/creating fertilizer", err);
          showAlert("error", "Some Error Occured");
        });
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Fertilizers")}
              </DialogTitle>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>

            {!isEditMode ? (
              <div style={{ ...scrollableContainerStyle }}>
                {fertilizerData !== null && fertilizerData.fertilizer_data && (
                  <Grid container spacing={2} style={{ padding: "0px" }}>
                    {fertilizerData.fertilizer_data.map((data, index) => (
                      <Grid item xs={12} key={index}>
                        <Card
                          style={{
                            background: "#F2FFF3",
                            borderRadius: "5px 5px 0px 0px",
                            border: "1px solid #6BAB43",
                          }}
                        >
                          <CardContent
                            style={{ padding: "0px", borderRadius: "10px" }}
                          >
                            <Grid
                              container
                              style={{
                                borderRadius: "5px 5px 0px 0px",
                                border: "1px solid #6BAB43",
                              }}
                            >
                              <Grid
                                item
                                xs={2}
                                style={{
                                  background: "#6BAB43",
                                  borderRadius: "10px;",
                                }}
                              >
                                <div style={{ borderRadius: "10px" }}>
                                  <Typography
                                    variant="h6"
                                    style={{
                                      fontSize: "26px",
                                      textAlign: "center",
                                      color: "white",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {data.days}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    style={{
                                      color: "white",
                                      fontSize: "10px",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("Days of Crop")}
                                  </Typography>
                                </div>
                              </Grid>
                              <Grid item xs={4} style={{ marginLeft: "0px" }}>
                                <Typography style={{ fontSize: "10px" }}>
                                  {t("Activity Date")}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  style={{ fontSize: "10px" }}
                                >
                                  {data.activity_date}
                                </Typography>
                                {data.fertilizers ? (
                                  <List>
                                    {data.fertilizers.map(
                                      (fertilizer, index) => (
                                        <ListItem key={index}>
                                          <ListItemText
                                            primary={
                                              <Typography
                                                style={{
                                                  fontSize: "10px",
                                                  marginLeft: "-15px",
                                                }}
                                              >
                                                {fertilizer.fertilizer_name}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                ) : (
                                  <Typography
                                    variant="subtitle2"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {t("No fertilizer data available")}.
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={4}>
                                <Typography style={{ fontSize: "10px" }}>
                                  {t("Recommend Date")}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  style={{ fontSize: "10px" }}
                                >
                                  {data.recommended_date}
                                </Typography>
                                {data.fertilizers ? (
                                  <List>
                                    {data.fertilizers.map(
                                      (fertilizer, index) => (
                                        <ListItem key={index}>
                                          <ListItemText
                                            primary={
                                              <Typography
                                                style={{
                                                  fontSize: "10px",
                                                  marginTop: "10px",
                                                }}
                                              >
                                                {fertilizer.dose}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                ) : (
                                  <Typography
                                    variant="subtitle2"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {t("No fertilizer data available")}.
                                  </Typography>
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{ background: "#6BAB43" }}
                              >
                                <div
                                  style={{
                                    marginTop: "1px",
                                    borderRadius: "0px",
                                  }}
                                >
                                  <img
                                    src={EditIcon}
                                    onClick={() => handleEditClick(data.id)}
                                    style={{ margin: "24px 10px 10px 12px" }}
                                  />
                                </div>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography style={{ fontSize: "10px" }}>
                                <span style={{ fontWeight: 700 }}>
                                  {t("Comment")}
                                </span>
                                :{data.comments}
                              </Typography>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </div>
            ) : null}

            {fertilizerData !== null && (
              <Grid container spacing={2} sx={{ marginTop: "10px" }}>
                {fertilizerData.current_fertilizer && (
                  <Grid item xs={12}>
                    <Card
                      style={{
                        background: "rgba(248, 235, 233, 0.20)",
                        border: "1px solid var(--bg-for-activ, #F8EBE9)",
                      }}
                    >
                      <CardContent>
                        <Grid container>
                          <Grid item xs={8}>
                            <Typography
                              variant="h6"
                              style={{
                                color: "var(--Brand-Color-Blue, #63AED2",
                                fontSize: "14px",
                              }}
                            >
                              {daysOfCrop}{" "}
                              {fertilizerData.current_fertilizer.length !== 0 ||
                              isEditMode
                                ? t("Days of Crop")
                                : ""}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              variant="subtitle1"
                              style={{ fontSize: "14px" }}
                            >
                              {recommendedDate}
                            </Typography>
                          </Grid>
                        </Grid>

                        {!isEditMode &&
                        fertilizerData.current_fertilizer &&
                        Array.isArray(
                          fertilizerData.current_fertilizer.fertilizers
                        ) &&
                        fertilizerData.current_fertilizer.fertilizers.length >
                          1 ? (
                          <Grid container>
                            <Grid item xs={7}>
                              <Typography
                                style={{
                                  textAlign: "center",
                                  fontSize: "10px",
                                }}
                              >
                                {t("Activity Date")}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <TextField
                                style={{ marginTop: "4px" }}
                                placeholder="DD/MM/YYYY"
                                value={
                                  selectedDate
                                    ? selectedDate.toLocaleDateString()
                                    : ""
                                }
                                InputProps={{
                                  sx: {
                                    height: "40px",
                                    borderRadius: "5px", // You can adjust this if needed
                                    marginTop: "10px",
                                    background: "#FFF",
                                    border: "1px solid #C7BDBD",
                                  },
                                  readOnly: true,
                                  onClick: () =>
                                    document
                                      .getElementById("date-picker")
                                      .click(),
                                }}
                              />
                            </Grid>
                          </Grid>
                        ) : null}
                        {!isEditMode &&
                        fertilizerData.current_fertilizer &&
                        Array.isArray(
                          fertilizerData.current_fertilizer.fertilizers
                        ) &&
                        fertilizerData.current_fertilizer.fertilizers &&
                        fertilizerData.current_fertilizer.fertilizers.length >
                          0 ? (
                          fertilizerData.current_fertilizer.fertilizers.map(
                            (fertilizer, index) => (
                              <>
                                <Grid container>
                                  <Grid item xs={4}>
                                    {!isEditMode &&
                                    fertilizerData.current_fertilizer &&
                                    Array.isArray(
                                      fertilizerData.current_fertilizer
                                        .fertilizers
                                    ) &&
                                    fertilizerData.current_fertilizer
                                      .fertilizers.length === 1 ? (
                                      <Typography
                                        style={{
                                          textAlign: "center",
                                          fontSize: "10px",
                                        }}
                                      >
                                        {t("Activity Date")}
                                      </Typography>
                                    ) : null}
                                  </Grid>
                                  <Grid
                                    item
                                    xs={
                                      fertilizerData.current_fertilizer
                                        .fertilizers.length > 1
                                        ? 10
                                        : 6
                                    }
                                  >
                                    <Typography style={{ fontSize: "10px" }}>
                                      {t("Recommendation per acre")}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      style={{
                                        fontSize: "12px",
                                        height: "1px",
                                      }}
                                    >
                                      {fertilizerNames[index]}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={1} style={{ height: "38px" }}>
                                    <Typography
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 800,
                                      }}
                                    >
                                      {" "}
                                      {dose[index]}Kg
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid container>
                                  <Grid
                                    item
                                    xs={5}
                                    style={{ marginLeft: "-10px" }}
                                  >
                                    {!isEditMode &&
                                    fertilizerData.current_fertilizer &&
                                    Array.isArray(
                                      fertilizerData.current_fertilizer
                                        .fertilizers
                                    ) &&
                                    fertilizerData.current_fertilizer
                                      .fertilizers.length === 1 ? (
                                      <div>
                                        <TextField
                                          style={{ marginTop: "39px" }}
                                          placeholder="DD/MM/YYYY"
                                          error={activityDateError}
                                          helperText={
                                            activityDateError
                                              ? "Activity date is required"
                                              : ""
                                          }
                                          value={
                                            selectedDate
                                              ? selectedDate.toLocaleDateString()
                                              : ""
                                          }
                                          InputProps={{
                                            sx: {
                                              height: "40px",
                                              borderRadius: "5px",
                                              marginTop: "10px",
                                              background: "#FFF",
                                              border: "1px solid #C7BDBD",
                                            },
                                            readOnly: true,
                                            onClick: () =>
                                              document
                                                .getElementById("date-picker")
                                                .click(),
                                          }}
                                        />
                                      </div>
                                    ) : null}
                                    <DatePicker
                                      id="date-picker"
                                      dateFormat="dd/MM/yyyy"
                                      selected={selectedDate}
                                      onChange={handleDateChange}
                                      className={classes.datepicker}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    xs={
                                      fertilizerData.current_fertilizer
                                        .fertilizers.length > 1
                                        ? 10
                                        : 6
                                    }
                                    style={{ marginTop: "5px" }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isChecked[index] || false}
                                          onChange={(event) =>
                                            handleCheck(event, index)
                                          }
                                          color="primary"
                                        />
                                      }
                                      label={t("Others")}
                                      style={{ fontSize: "12px" }}
                                    />
                                    <Grid item xs={11}>
                                      <FormControl fullWidth>
                                        <Select
                                          disabled={!isChecked[index]}
                                          onChange={(event) =>
                                            handleFertilizerChange(event, index)
                                          }
                                          value={
                                            userSelectFertilizer[index] || ""
                                          }
                                          sx={{
                                            height: "40px",
                                            border: "1px solid #9C9FA0",
                                            borderRadius: "5px",
                                          }}
                                          error={
                                            errors.userSelectFertilizerError !==
                                            undefined
                                          }
                                        >
                                          {fertilizerDropdown.map(
                                            (fertilizer, index) => (
                                              <MenuItem
                                                key={index}
                                                value={fertilizer.name}
                                              >
                                                {fertilizer.name}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  </Grid>
                                  <Grid itme xs={1}>
                                    <TextField
                                      style={{ marginTop: "46px" }}
                                      disabled={!isChecked[index]}
                                      onChange={(event) =>
                                        handleDoseChange(event, index)
                                      }
                                      value={userDose[index] || ""}
                                      error={errors.userDoseError !== undefined}
                                      helperText={errors.userDoseError}
                                      InputProps={{
                                        sx: {
                                          height: "40px",
                                          borderRadius: "5px", // You can adjust this if needed
                                          marginTop: "30px",
                                          background: "#FFF",
                                          border: "1px solid #C7BDBD",
                                          margin: "auto",
                                          display: "flex",
                                          width: "40px",
                                          justifyContent: "center",
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </>
                            )
                          )
                        ) : !isEditMode ? (
                          <p>{t("You have reach the Final Stage")} </p>
                        ) : null}

                        {isEditMode &&
                          editedFertilizer.map((fertilizerData, index) => (
                            <div key={index} style={{ marginTop: "20px" }}>
                              {isEditMode &&
                              fertilizerData.fertilizers.length > 2 ? (
                                <Grid container>
                                  <Grid item xs={6}>
                                    <Typography
                                      style={{
                                        textAlign: "center",
                                        fontSize: "10px",
                                        marginTop: "20px",
                                      }}
                                    >
                                      {t("Activity Date")}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      style={{ marginTop: "4px" }}
                                      placeholder="DD/MM/YYYY"
                                      value={
                                        selectedDate
                                          ? selectedDate.toLocaleDateString()
                                          : ""
                                      }
                                      InputProps={{
                                        sx: {
                                          height: "40px",
                                          borderRadius: "5px", // You can adjust this if needed
                                          marginTop: "10px",
                                          background: "#FFF",
                                          border: "1px solid #C7BDBD",
                                        },
                                        readOnly: true,
                                        onClick: () =>
                                          document
                                            .getElementById("date-picker")
                                            .click(),
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              ) : null}
                              {fertilizerData.fertilizers.map(
                                (fertilizer, innerIndex) => (
                                  <React.Fragment key={innerIndex}>
                                    {fertilizer.type === "recommend" && (
                                      <>
                                        <Grid container>
                                          <Grid item xs={4}>
                                            <Typography
                                              style={{
                                                textAlign: "center",
                                                fontSize: "10px",
                                                marginTop: "20px",
                                              }}
                                            >
                                              {t("Activity Date")}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <Typography
                                              style={{ fontSize: "10px" }}
                                            >
                                              {t("Recommendation per acre")}
                                            </Typography>
                                            <Typography
                                              variant="body1"
                                              style={{ fontSize: "12px" }}
                                            >
                                              {fertilizer.fertilizer_name}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <Typography
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: 800,
                                              }}
                                            >
                                              {" "}
                                              {fertilizer.dose}Kg
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                        <Grid container>
                                          {isEditMode &&
                                          fertilizerData.fertilizers.length <=
                                            2 ? (
                                            <Grid item xs={4}>
                                              <TextField
                                                style={{ marginTop: "39px" }}
                                                placeholder="DD/MM/YYYY"
                                                error={activityDateError}
                                                helperText={
                                                  activityDateError
                                                    ? "Activity date is required"
                                                    : ""
                                                }
                                                value={
                                                  selectedDate
                                                    ? selectedDate.toLocaleDateString()
                                                    : ""
                                                }
                                                InputProps={{
                                                  sx: {
                                                    height: "40px",
                                                    borderRadius: "5px",
                                                    marginTop: "10px",
                                                    background: "#FFF",
                                                    border: "1px solid #C7BDBD",
                                                  },
                                                  readOnly: true,
                                                  onClick: () =>
                                                    document
                                                      .getElementById(
                                                        "date-picker"
                                                      )
                                                      .click(),
                                                }}
                                              />
                                            </Grid>
                                          ) : null}
                                          <DatePicker
                                            id="date-picker"
                                            dateFormat="dd/MM/yyyy"
                                            selected={selectedDate}
                                            onChange={handleDateChange}
                                            className={classes.datepicker}
                                          />
                                          <Grid
                                            item
                                            xs={
                                              fertilizerData.fertilizers
                                                .length > 2
                                                ? 10
                                                : 6
                                            }
                                          >
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={
                                                    isChecked[innerIndex] ||
                                                    false
                                                  }
                                                  onChange={(event) =>
                                                    handleCheck(
                                                      event,
                                                      innerIndex
                                                    )
                                                  }
                                                  color="primary"
                                                />
                                              }
                                              label={t("Others")}
                                              style={{ fontSize: "12px" }}
                                            />
                                            {/* Your Select Component for recommended fertilizer */}
                                            <Grid item xs={10}>
                                              <FormControl fullWidth>
                                                <Select
                                                  disabled={
                                                    !isChecked[innerIndex]
                                                  }
                                                  onChange={(event) =>
                                                    handleFertilizerChange(
                                                      event,
                                                      innerIndex
                                                    )
                                                  }
                                                  value={
                                                    userSelectFertilizer[
                                                      innerIndex
                                                    ] || ""
                                                  }
                                                  sx={{
                                                    height: "40px",
                                                    border: "1px solid #9C9FA0",
                                                    borderRadius: "5px",
                                                  }}
                                                >
                                                  {/* Map and render MenuItems for recommended fertilizer dropdown */}
                                                  {fertilizerDropdown.map(
                                                    (
                                                      fertilizerItem,
                                                      dropdownIndex
                                                    ) => (
                                                      <MenuItem
                                                        key={dropdownIndex}
                                                        value={
                                                          fertilizerItem.name
                                                        }
                                                      >
                                                        {fertilizerItem.name}
                                                      </MenuItem>
                                                    )
                                                  )}
                                                </Select>
                                              </FormControl>
                                            </Grid>
                                          </Grid>
                                          <Grid itme xs={2}>
                                            <TextField
                                              style={{ marginTop: "60px" }}
                                              disabled={!isChecked[innerIndex]}
                                              onChange={(event) =>
                                                handleDoseChange(
                                                  event,
                                                  innerIndex
                                                )
                                              }
                                              value={userDose[innerIndex] || ""}
                                              InputProps={{
                                                sx: {
                                                  height: "40px",
                                                  borderRadius: "5px",
                                                  marginTop: "30px",
                                                  background: "#FFF",
                                                  border: "1px solid #C7BDBD",
                                                  margin: "auto",
                                                  display: "flex",
                                                  width: "40px",
                                                  justifyContent: "center",
                                                },
                                              }}
                                            />
                                          </Grid>
                                        </Grid>
                                      </>
                                    )}
                                  </React.Fragment>
                                )
                              )}
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </div>

        <DialogActions style={{ display: "grid" }}></DialogActions>
        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment (Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
          />
        </div>

        <div
          style={{
            display: "flex",
            margin: "auto",
            justifyContent: "center",
            width: "140px",
          }}
        >
          <CustomButton onClick={handleSubmit} hideIcon>
            {t("Submit")}
          </CustomButton>
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default FertilizerPopup;
